import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export interface AppSettings {
  id: string;
  maintenance_mode: boolean;
  platform_fee: number;
  tax_rate: number;
  currency: string;
  created_at?: string;
  updated_at?: string;
}

export interface UpdateAppSettingsInput {
  maintenance_mode?: boolean;
  platform_fee?: number;
  tax_rate?: number;
  currency?: string;
}

const DEFAULT_APP_SETTINGS: Omit<AppSettings, 'id' | 'created_at' | 'updated_at'> = {
  maintenance_mode: false,
  platform_fee: 10,
  tax_rate: 10,
  currency: 'AUD'
};

export function useAppSettings() {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const { data: settings, isLoading, error } = useQuery({
    queryKey: ["appSettings"],
    queryFn: async () => {
      try {
        // Try to get settings from the app_settings table
        const { data: existingSettings, error: fetchError } = await supabase
          .from("app_settings")
          .select("*")
          .eq("id", "global")
          .maybeSingle();

        if (fetchError) {
          console.error("Error fetching app settings:", fetchError);
          // Return default settings instead of throwing
          return { 
            id: 'global', 
            ...DEFAULT_APP_SETTINGS
          } as AppSettings;
        }

        // If we don't have settings yet, create default settings if we're admin
        if (!existingSettings && isAdmin) {
          console.log("No settings found, attempting to create default settings");

          const newSettings = {
            ...DEFAULT_APP_SETTINGS,
            id: 'global', // We'll use a singleton approach with a fixed ID
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };

          try {
            const { error: insertError } = await supabase
              .from("app_settings")
              .insert(newSettings);

            if (insertError) {
              console.error("Error creating default app settings:", insertError);
              // Return default settings instead of throwing
              return { 
                id: 'global', 
                ...DEFAULT_APP_SETTINGS
              } as AppSettings;
            }

            return newSettings as AppSettings;
          } catch (err) {
            console.error("Exception creating settings:", err);
            // Return default settings in case of any error
            return { 
              id: 'global', 
              ...DEFAULT_APP_SETTINGS
            } as AppSettings;
          }
        }

        // If we have settings, return them, otherwise return defaults
        return existingSettings as AppSettings || {
          id: 'global',
          ...DEFAULT_APP_SETTINGS
        } as AppSettings;
      } catch (err) {
        console.error("Unexpected error in useAppSettings:", err);
        // Always return something usable
        return { 
          id: 'global', 
          ...DEFAULT_APP_SETTINGS
        } as AppSettings;
      }
    }
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: UpdateAppSettingsInput) => {
      console.log("Updating app settings with:", updates);

      if (!isAdmin) {
        throw new Error("You don't have permission to update application settings");
      }

      const updatedSettings = {
        ...updates,
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from("app_settings")
        .update(updatedSettings)
        .eq("id", "global");

      if (error) {
        console.error("Error updating app settings:", error);
        throw error;
      }

      return { ...settings, ...updates };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["appSettings"] });
      toast.success("Application settings updated successfully");
    },
    onError: (error: Error) => {
      console.error("Failed to update app settings:", error);
      toast.error(`Failed to update settings: ${error.message}`);
    }
  });

  return {
    settings: settings || { ...DEFAULT_APP_SETTINGS, id: 'global' } as AppSettings,
    isLoading,
    error,
    updateSettings,
    isAdmin
  };
} 